import React, { useState, useEffect, useCallback } from 'react';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import {
  Card, Button, message, Space, Typography, Form, Select, Input, DatePicker, Row, Col, Divider, Table, Tag
} from 'antd';
import { UserOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { fetchCashRegisters, type CashRegisterDto } from '../services/cashService';
import { api } from '../services/api';
import AccountSelectModal from '../components/AccountSelectModal';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface PaymentMethod {
  id: number;
  paymentName: string;
  paymentCode: string;
}

interface SelectedAccount {
  id: number;
  accountCode: string;
  accountName: string;
  firstName: string;
  lastName: string;
  companyName?: string;
}

interface PaymentHistoryItem {
  id: number;
  entryDate: string;
  amount: number;
  description?: string | null;
  receiptNo?: string | null;
  isServiceOrderPayment: boolean;
  serviceOrderNo?: string | null;
  paymentMethodName?: string | null;
  cashRegisterName?: string | null;
}

interface PaymentHistorySection {
  items: PaymentHistoryItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalAmount: number;
}

interface PaymentHistoryResponse {
  accountId: number;
  accountName: string;
  balance: number;
  collections: PaymentHistorySection;
  disbursements: PaymentHistorySection;
}

const PAGE_SIZE = 10;

const formatMoney = (v: number) =>
  (v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const historyColumns: ColumnsType<PaymentHistoryItem> = [
  {
    title: 'Tarih',
    dataIndex: 'entryDate',
    key: 'entryDate',
    width: 130,
    render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-')},
  {
    title: 'Tutar (₺)',
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'right',
    render: (v: number) => formatMoney(v)},
  {
    title: 'Makbuz no',
    dataIndex: 'receiptNo',
    key: 'receiptNo',
    width: 130,
    render: (v: string | null) => v ?? '-'},
  {
    title: 'Ödeme türü',
    dataIndex: 'paymentMethodName',
    key: 'paymentMethodName',
    width: 120,
    render: (v: string | null) => v ?? '-'},
  {
    title: 'Kasa',
    dataIndex: 'cashRegisterName',
    key: 'cashRegisterName',
    width: 110,
    render: (v: string | null) => v ?? '-'},
  {
    title: 'Servis fişi',
    dataIndex: 'serviceOrderNo',
    key: 'serviceOrderNo',
    width: 110,
    render: (v: string | null, row) =>
      v ? (
        <Tag color="blue">{v}</Tag>
      ) : row.isServiceOrderPayment ? (
        '-'
      ) : (
        '-'
      )},
  {
    title: 'Açıklama',
    dataIndex: 'description',
    key: 'description',
    ellipsis: true,
    render: (v: string | null) => v ?? '-'},
];

/** Cari tahsilat / tedarikçi ödemesi (servis fişi tahsilatı servis fişi ekranından). */
const Payment: React.FC = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegisterDto[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSupplierPaymentMode = new URLSearchParams(location.search).get('mode') === 'payable';

  const [historyDateRange, setHistoryDateRange] = useState<[Dayjs, Dayjs] | null>(() => [
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<PaymentHistoryResponse | null>(null);
  const [collectionPage, setCollectionPage] = useState(1);
  const [disbursementPage, setDisbursementPage] = useState(1);

  const loadPaymentMethodsAndRegisters = async () => {
    try {
      const [pmRes, crRes] = await Promise.all([
        api.get<PaymentMethod[]>('/paymentmethods'),
        fetchCashRegisters(),
      ]);
      setPaymentMethods(pmRes.data || []);
      setCashRegisters(crRes.filter((r: CashRegisterDto) => r.isActive !== false));
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Veri yüklenirken hata');
    }
  };

  const loadPaymentHistory = useCallback(async (pageOverride?: { collectionPage?: number; disbursementPage?: number }) => {
    if (!selectedAccount) {
      setHistory(null);
      return;
    }
    setHistoryLoading(true);
    try {
      const params: Record<string, string | number> = {
        collectionPage: pageOverride?.collectionPage ?? collectionPage,
        disbursementPage: pageOverride?.disbursementPage ?? disbursementPage,
        pageSize: PAGE_SIZE};
      if (historyDateRange?.[0]) params.startDate = historyDateRange[0].format('YYYY-MM-DD');
      if (historyDateRange?.[1]) params.endDate = historyDateRange[1].format('YYYY-MM-DD');
      const { data } = await api.get<PaymentHistoryResponse>(
        `/accounts/${selectedAccount.id}/payment-history`,
        { params }
      );
      setHistory(data);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ödeme geçmişi yüklenirken hata');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedAccount, historyDateRange, collectionPage, disbursementPage]);

  useEffect(() => {
    loadPaymentMethodsAndRegisters();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const accountIdParam = searchParams.get('accountId');
    if (!accountIdParam) return;
    const accountId = parseInt(accountIdParam, 10);
    if (!accountId) return;

    const loadAccount = async () => {
      try {
        const { data } = await api.get('/accounts/' + accountId);
        const acc: SelectedAccount = {
          id: data.id,
          accountCode: data.accountCode,
          accountName: data.accountName,
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName};
        setSelectedAccount(acc);
      } catch (e: any) {
        message.error(e.response?.data?.message || 'Cari bilgisi yüklenirken hata');
      }
    };

    loadAccount();
  }, [location.search]);

  useEffect(() => {
    if (paymentMethods.length > 0 && form.getFieldValue('paymentMethodId') == null) {
      form.setFieldValue('paymentMethodId', paymentMethods[0].id);
    }
    if (cashRegisters.length > 0 && form.getFieldValue('cashRegisterId') == null) {
      const def = cashRegisters.find((c) => c.isDefault) || cashRegisters[0];
      if (def) form.setFieldValue('cashRegisterId', def.id);
    }
  }, [paymentMethods, cashRegisters, form]);

  useEffect(() => {
    setCollectionPage(1);
    setDisbursementPage(1);
  }, [selectedAccount?.id, historyDateRange]);

  useEffect(() => {
    loadPaymentHistory();
  }, [loadPaymentHistory]);

  const handleAccountSelect = (account: SelectedAccount) => {
    setSelectedAccount(account);
    setAccountModalVisible(false);
  };

  const handleCariPaymentSubmit = async () => {
    if (!selectedAccount) {
      message.warning('Lütfen cari seçin');
      return;
    }
    try {
      const values = await form.validateFields();
      if (values.amount <= 0) {
        message.warning("Tutar 0'dan büyük olmalı");
        return;
      }
      setSaving(true);
      await api.post(`/accounts/${selectedAccount.id}/payments`, {
        paymentMethodId: values.paymentMethodId,
        amount: values.amount,
        paymentDate: values.paymentDate ? values.paymentDate.toISOString() : undefined,
        receiptNo: values.receiptNo || undefined,
        description: values.description || undefined,
        cashRegisterId: values.cashRegisterId ?? undefined,
        isSupplierPayment: isSupplierPaymentMode});
      message.success(isSupplierPaymentMode ? 'Tedarikçi ödemesi kaydedildi' : 'Tahsilat kaydedildi');
      form.setFieldsValue({
        amount: undefined,
        receiptNo: undefined,
        description: undefined,
        paymentDate: dayjs()});
      setCollectionPage(1);
      setDisbursementPage(1);
      await loadPaymentHistory();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ödeme kaydedilirken hata');
    } finally {
      setSaving(false);
    }
  };

  const handleHistoryFilter = () => {
    setCollectionPage(1);
    setDisbursementPage(1);
    loadPaymentHistory({ collectionPage: 1, disbursementPage: 1 });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title={isSupplierPaymentMode ? 'Cari ödeme (tedarikçi)' : 'Cari tahsilat'}>
        <Text type="secondary">
          {isSupplierPaymentMode
            ? 'Seçtiğiniz cariye ödeme girebilirsiniz. Kasa bakiyesi azalır ve cari borcu düşer.'
            : 'Seçtiğiniz cariden tahsilat girebilirsiniz. Kasa bakiyesi artar ve cari alacağı düşer.'}
        </Text>
        <Divider />
        <Form form={form} layout="vertical" onFinish={handleCariPaymentSubmit} initialValues={{ paymentDate: dayjs() }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Cari" required>
                <Button
                  type="default"
                  block
                  icon={<UserOutlined />}
                  onClick={() => setAccountModalVisible(true)}
                >
                  {selectedAccount
                    ? `${selectedAccount.accountCode} - ${selectedAccount.accountName}`
                    : 'Cari seç'}
                </Button>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar girin' }]}>
                <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMethodId" label="Ödeme türü" rules={[{ required: true }]}>
                <Select
                  placeholder="Seçin"
                  options={paymentMethods.map((pm) => ({ label: pm.paymentName, value: pm.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="paymentDate" label="Tarih">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="receiptNo" label="Makbuz no">
                <Input placeholder="Boş bırakılırsa otomatik" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cashRegisterId" label="Kasa">
                <Select
                  placeholder="Kasa seçin"
                  allowClear
                  options={cashRegisters.map((c) => ({ label: c.registerName, value: c.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Açıklama">
                <Input.TextArea rows={1} placeholder="Opsiyonel" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} disabled={!selectedAccount}>
                  {isSupplierPaymentMode ? 'Ödeme yap' : 'Tahsilat yap'}
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setSelectedAccount(null);
                    setHistory(null);
                  }}
                >
                  Temizle
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {selectedAccount && (
        <Card style={{ marginTop: 16 }} title={`Ödeme geçmişi — ${selectedAccount.accountName}`}>
          <Space wrap style={{ marginBottom: 16 }} align="center">
            <Text>Tarih aralığı:</Text>
            <RangePicker
              value={historyDateRange}
              onChange={(dates) => setHistoryDateRange(dates as [Dayjs, Dayjs] | null)}
              format="DD.MM.YYYY"
              allowClear
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleHistoryFilter} loading={historyLoading}>
              Filtrele
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadPaymentHistory()} loading={historyLoading}>
              Yenile
            </Button>
            {history != null && (
              <Text type="secondary">
                Cari bakiye:{' '}
                <Text strong style={{ color: history.balance > 0 ? '#cf1322' : history.balance < 0 ? '#389e0d' : undefined }}>
                  {formatMoney(history.balance)} ₺
                </Text>
              </Text>
            )}
          </Space>

          <Title level={5} style={{ marginTop: 8 }}>
            {isSupplierPaymentMode ? 'Bu cariden alınan tahsilatlar' : 'Bu cariden gelen ödemeler (tahsilat)'}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Cari tahsilat ve servis fişi tahsilatları
            {history?.collections != null && (
              <> — dönem toplamı: <Text strong>{formatMoney(history.collections.totalAmount)} ₺</Text></>
            )}
          </Text>
          <Table<PaymentHistoryItem>
            rowKey="id"
            size="small"
            loading={historyLoading}
            columns={historyColumns}
            dataSource={history?.collections.items ?? []}
            pagination={{
              current: collectionPage,
              pageSize: PAGE_SIZE,
              total: history?.collections.totalCount ?? 0,
              showSizeChanger: false,
              showTotal: (total) => `${total} kayıt`,
              onChange: (page) => setCollectionPage(page)}}
          />

          <Divider />

          <Title level={5}>
            {isSupplierPaymentMode ? 'Bu cariye yapılan ödemeler' : 'Bu cariye yapılan ödemeler (varsa)'}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Tedarikçi / cari ödeme fişleri
            {history?.disbursements != null && (
              <> — dönem toplamı: <Text strong>{formatMoney(history.disbursements.totalAmount)} ₺</Text></>
            )}
          </Text>
          <Table<PaymentHistoryItem>
            rowKey="id"
            size="small"
            loading={historyLoading}
            columns={historyColumns}
            dataSource={history?.disbursements.items ?? []}
            pagination={{
              current: disbursementPage,
              pageSize: PAGE_SIZE,
              total: history?.disbursements.totalCount ?? 0,
              showSizeChanger: false,
              showTotal: (total) => `${total} kayıt`,
              onChange: (page) => setDisbursementPage(page)}}
          />
        </Card>
      )}

      <AccountSelectModal
        visible={accountModalVisible}
        onClose={() => setAccountModalVisible(false)}
        onSelect={handleAccountSelect}
      />
    </div>
  );
};

export default Payment;
